import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

const BurpSuiteViewDashboard = ({ dastScanData, webURL }) => {
  console.log("DAST Scan Data:", dastScanData);
  const [criticalIssues, setCriticalIssues] = useState(0);
  const [highIssues, setHighIssues] = useState(0);
  const [mediumIssues, setMediumIssues] = useState(0);
  const [lowIssues, setLowIssues] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const options = ["All", "Critical", "High", "Medium", "Low"];

  const [value, setValue] = useState("All");
  const [selectedSeverity, setSelectedSeverity] = useState("All");

  const [open, setOpen] = useState(true);

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setCriticalIssues(
      dastScanData.filter((item) => item.severity === "critical").length
    );
    setHighIssues(
      dastScanData.filter((item) => item.severity === "high").length
    );
    setMediumIssues(
      dastScanData.filter((item) => item.severity === "medium").length
    );
    setLowIssues(dastScanData.filter((item) => item.severity === "low").length);
  }, [dastScanData]);

  const severityData = [
    { label: "CRITICAL", count: criticalIssues, color: "#AE241E" },
    { label: "HIGH", count: highIssues, color: "#FF4040" },
    { label: "MEDIUM", count: mediumIssues, color: "#FFA500" },
    { label: "LOW", count: lowIssues, color: "#388E3C" },
  ];

  const SeverityCircle = ({ label, count, color }) => (
    <Box
      sx={{
        width: 170,
        height: 170,
        borderRadius: "50%",
        backgroundColor: "white",
        position: "relative",
        mx: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: `12px 0 0 0 ${color}`, // Crescent on the right side
      }}
    >
      <Box
        sx={{
          textAlign: "center",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            fontSize: 26,
            color: count === 0 ? "#757575" : color,
          }}
        >
          {count}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
            fontSize: 14,
            color: "black",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );

  const formatSolutionList = (solutionText) => {
    if (!solutionText || typeof solutionText !== "string") return [];

    // Split by known phrases or newlines or periods followed by a space
    const splitPatterns = [
      /\bPhase: .*?(?=Phase: |$)/gs, // Capture text starting with "Phase: ..."
      /\.\s+(?=[A-Z])/g, // Split at end of sentence where next word starts with capital
      /\n/g, // Or just newline
    ];

    let parts = [];

    for (let pattern of splitPatterns) {
      parts = solutionText
        .split(pattern)
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.length > 1) break; // use the first successful split that gives >1 item
    }

    return parts;
  };

  const formatReferences = (refString) => {
    if (!refString || typeof refString !== "string") return [];

    // Match all https URLs
    const refs = refString.match(/https:\/\/[^\s,]+/g);

    // If no URLs found, return the whole string as a single item
    return refs?.length ? refs : [refString.trim()];
  };

  // Heighlight the matched text in the fields
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <mark
          key={i}
          style={{ backgroundColor: "#e0e0e0", fontWeight: "bold" }}
        >
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const formatDescription = (text, searchTerm) => {
    if (!text) return null;

    const lines = text.split(/\n|(?=\s*\*)/g); // split by new line or bullet points

    return lines.map((line, i) => {
      const trimmed = line.trim();

      // Find quoted text and bold it
      const parts = [];
      const regex = /(["'])(?:(?=(\\?))\2.)*?\1/g; // match quoted string
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(trimmed)) !== null) {
        const before = trimmed.slice(lastIndex, match.index);
        if (before) parts.push(highlightMatch(before, searchTerm));

        const quoted = match[0].slice(1, -1); // remove quotes
        parts.push(
          <strong key={`quoted-${i}-${match.index}`}>
            {highlightMatch(quoted, searchTerm)}
          </strong>
        );

        lastIndex = regex.lastIndex;
      }

      const after = trimmed.slice(lastIndex);
      if (after) parts.push(highlightMatch(after, searchTerm));

      if (trimmed.startsWith("*")) {
        return (
          <li key={i} style={{ marginBottom: "6px", marginLeft: "20px" }}>
            {parts}
          </li>
        );
      }

      return (
        <p key={i} style={{ margin: "6px 0" }}>
          {parts}
        </p>
      );
    });
  };
  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={handleClose}>
      <DialogContent sx={{ backgroundColor: "#f6f3f2" }}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box p={3} sx={{ backgroundColor: "#f6f3f2", minHeight: "100vh" }}>
          {/************ Header ***********/}
          <Box sx={{ backgroundColor: "#fff", p: 2, borderRadius: 2 }}>
            <Typography
              sx={{
                fontSize: "27px",
                fontWeight: "700",
              }}
            >
              DAST Dashboard Report
            </Typography>
            <Typography sx={{ fontSize: "16px", mt: 1 }}>
              Target URL: {webURL}
            </Typography>
          </Box>

          {/******** Summary Cards *******/}
          <Grid container justifyContent="space-around" spacing={3} mt={3}>
            {severityData.map((item, index) => (
              <Grid
                item
                key={index}
                xs={12}
                sm={6}
                md={3}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SeverityCircle
                  label={item.label}
                  count={item.count}
                  color={item.color}
                />
              </Grid>
            ))}
          </Grid>

          {/************ Vulnerability Table ***************/}
          <Box sx={{ width: "100%", mt: 4, ml: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography
                mt={4}
                mb={3}
                sx={{ fontSize: "24px", fontWeight: "500" }}
              >
                Vulnerabilities by Name and Severity
              </Typography>
              <FormControl sx={{ width: 155, height: 42, mb: 0.5, ml: 2 }}>
                <Select
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  displayEmpty
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
                    ".MuiSelect-select": {
                      padding: "10px 14px",
                      fontSize: "16px",
                      fontWeight: 500,
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "12px",
                        boxShadow: "0px 1px 6px rgba(0, 0, 0, 0.1)",
                        mt: 1,
                      },
                    },
                  }}
                >
                  {options.map((opt) => (
                    <MenuItem
                      key={opt}
                      value={opt}
                      sx={{
                        fontSize: "16px",
                        paddingY: "10px",
                        "&.Mui-selected": {
                          backgroundColor: "transparent",
                          // outline: "2px solid #3B82F6", // Blue border for selected
                        },
                        "&:hover": {
                          backgroundColor: "#F3F4F6",
                        },
                      }}
                    >
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TableContainer
              component={Paper}
              sx={{ minHeight: "1vh", maxHeight: "70vh", overflowY: "auto" }}
            >
              <Table stickyHeader sx={{ border: "1px solid #ddd" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: "#0085DE",
                        color: "white",
                        border: "1px solid #ddd",
                        fontSize: "14px",
                      }}
                    >
                      S.No
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#0085DE",
                        color: "white",
                        border: "1px solid #ddd",
                        fontSize: "14px",
                      }}
                    >
                      Vulnerabilities by Name
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#0085DE",
                        color: "white",
                        border: "1px solid #ddd",
                        fontSize: "14px",
                      }}
                    >
                      Severity
                    </TableCell>
                    {/* <TableCell
                      sx={{
                        backgroundColor: "#0085DE",
                        color: "white",
                        border: "1px solid #ddd",
                        fontSize: "14px",
                      }}
                    >
                      CWE ID
                    </TableCell> */}
                  </TableRow>
                </TableHead>

                <TableBody
                  sx={{
                    minHeight: "1vh",
                    maxHeight: "10vh",
                    overflowY: "auto",
                  }}
                >
                  {dastScanData.filter(
                    (vuln) =>
                      vuln.severity !== "info" &&
                      (value === "All" || vuln.severity === value)
                  ).length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align="center"
                        sx={{ fontSize: "16px", padding: "20px" }}
                      >
                        No vulnerabilities found for selected severity.
                      </TableCell>
                    </TableRow>
                  ) : (
                    dastScanData
                      .filter(
                        (vuln) =>
                          vuln.severity !== "info" &&
                          (value === "All" || vuln.severity === value)
                      )
                      .sort((a, b) => {
                        const severityOrder = {
                          Critical: 1,
                          High: 2,
                          Medium: 3,
                          Low: 4,
                        };
                        return (
                          severityOrder[a.severity] - severityOrder[b.severity]
                        );
                      })
                      .map((vuln, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor:
                              index % 2 === 0 ? "#FFFFFF" : "#F0F9FF",
                          }}
                        >
                          <TableCell
                            sx={{ border: "1px solid #ddd", fontSize: "14px" }}
                          >
                            {index + 1}
                          </TableCell>
                          <TableCell
                            sx={{ border: "1px solid #ddd", fontSize: "14px" }}
                          >
                            {vuln.name}
                          </TableCell>
                          <TableCell
                            sx={{ border: "1px solid #ddd", fontSize: "14px" }}
                          >
                            <Chip
                              label={vuln.severity}
                              size="small"
                              sx={{
                                fontSize: "14px",
                                backgroundColor:
                                  vuln.severity === "critical"
                                    ? "#AE241E"
                                    : vuln.severity === "high"
                                    ? "#FF4040"
                                    : vuln.severity === "medium"
                                    ? "#FFA500"
                                    : vuln.severity === "low"
                                    ? "#388E3C"
                                    : "#9E9E9E",
                                borderRadius: "8px",
                                width: "86px",
                                height: "32px",
                                color: "#fff",
                                textTransform: "capitalize",
                              }}
                            />
                          </TableCell>
                          {/* <TableCell
                            sx={{ border: "1px solid #ddd", fontSize: "14px" }}
                          >
                            {vuln.cweid}
                          </TableCell> */}
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/************  Vulnerability Details ***********/}
          <Box
            sx={{
              width: "100%",
              backgroundColor: "#fff",
              p: 2,
              borderRadius: 2,
              mt: 6,
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: "24px", fontWeight: "700" }}>
                Vulnerabilities Details
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-around",
                  alignItems: "center",
                }}
              >
                <TextField
                  placeholder="Search"
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ height: 42, borderRadius: 12 }} // Optional: adjust width
                />
                <FormControl sx={{ width: 155, height: 42, mb: 0.5, ml: 2 }}>
                  <Select
                    value={selectedSeverity}
                    onChange={(e) => setSelectedSeverity(e.target.value)}
                    displayEmpty
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "12px",
                      boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
                      ".MuiSelect-select": {
                        padding: "10px 14px",
                        fontSize: "16px",
                        fontWeight: 500,
                      },
                      "& fieldset": {
                        border: "none",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: "12px",
                          boxShadow: "0px 1px 6px rgba(0, 0, 0, 0.1)",
                          mt: 1,
                        },
                      },
                    }}
                  >
                    {options.map((opt) => (
                      <MenuItem
                        key={opt}
                        value={opt}
                        sx={{
                          fontSize: "16px",
                          paddingY: "10px",
                          "&.Mui-selected": {
                            backgroundColor: "transparent",
                            // outline: "2px solid #3B82F6", // Blue border for selected
                          },
                          "&:hover": {
                            backgroundColor: "#F3F4F6",
                          },
                        }}
                      >
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box
              sx={{ minHeight: "1vh", maxHeight: "70vh", overflowY: "auto" }}
            >
              {Array.isArray(dastScanData) ? (
                dastScanData.filter(
                  (vuln) =>
                    vuln.severity !== "info" &&
                    (selectedSeverity === "All" ||
                      vuln.severity === selectedSeverity)
                ).length > 0 ? (
                  dastScanData
                    .filter(
                      (vuln) =>
                        vuln.severity !== "info" &&
                        (selectedSeverity === "All" ||
                          vuln.severity === selectedSeverity)
                    )
                    .sort((a, b) => {
                      const severityOrder = {
                        Critical: 1,
                        High: 2,
                        Medium: 3,
                        Low: 4,
                      };
                      return (
                        severityOrder[a.severity] - severityOrder[b.severity]
                      );
                    })
                    .map((vuln, index) => (
                      <Box key={index} my={3}>
                        <Divider
                          sx={{
                            mt: 4,
                            mb: 4,
                            borderColor: "#CACACA",
                            borderBottomWidth: "1px",
                          }}
                        />

                        {/* Title */}
                        <Box
                          display="flex"
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="center"
                          gap={2}
                        >
                          <Typography
                            sx={{ fontSize: "22px", fontWeight: "700" }}
                          >
                            {index + 1}.{highlightMatch(vuln.name, searchTerm)}
                          </Typography>
                          <Chip
                            label={vuln.severity}
                            size="small"
                            sx={{
                              fontSize: "14px",
                              backgroundColor:
                                vuln.severity === "critical"
                                  ? "#AE241E"
                                  : vuln.severity === "high"
                                  ? "#FF4040"
                                  : vuln.severity === "medium"
                                  ? "#FFA500"
                                  : vuln.severity === "low"
                                  ? "#388E3C"
                                  : "#9E9E9E",
                              borderRadius: "8px",
                              width: "86px",
                              height: "32px",
                              color: "#fff",
                              textTransform: "capitalize",
                            }}
                          />
                        </Box>

                        {/* Description */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            mt: 3,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "20px",
                              fontWeight: "700",
                              color: "#666875",
                            }}
                          >
                            Description
                          </Typography>
                          <Typography
                            sx={{ fontSize: "15px", color: "#666875", mt: 2 }}
                          >
                            {vuln.description
                              ? formatDescription(vuln.description, searchTerm)
                              : formatDescription(
                                  vuln.issue_background,
                                  searchTerm
                                )}
                          </Typography>
                        </Box>

                        {/* <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            mt: 3,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "20px",
                              fontWeight: "700",
                              color: "#666875",
                            }}
                          >
                            Instances ({vuln.instances.length})
                          </Typography>
                          <TableContainer
                            component={Paper}
                            sx={{
                              borderRadius: 3, // outer rounded corners
                              backgroundColor: "#f5f5f5",
                              mt: 2,
                              minHeight: "1vh",
                              maxHeight: "50vh", // Adjust height as needed
                              overflow: "auto", // to apply rounded border
                            }}
                          >
                            <Table stickyHeader>
                              <TableHead>
                                <TableRow sx={{ backgroundColor: "#d9d7d7" }}>
                                  <TableCell
                                    sx={{
                                      fontWeight: "bold",
                                      width: "5%",
                                      backgroundColor: "#d9d7d7",
                                    }}
                                  >
                                    #
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: "bold",
                                      width: "45%",
                                      backgroundColor: "#d9d7d7",
                                    }}
                                  >
                                    URL
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: "bold",
                                      backgroundColor: "#d9d7d7",
                                    }}
                                  >
                                    Evidence
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody
                                sx={{
                                  minHeight: "1px",
                                  maxHeight: "10vh",
                                  overflowY: "auto",
                                }}
                              >
                                {vuln.instances && vuln.instances.length > 0 ? (
                                  vuln.instances.map((instance, i) => (
                                    <TableRow key={i}>
                                      <TableCell>{instance.Instance}</TableCell>
                                      <TableCell>
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            color: "#49A3FF",
                                            wordBreak: "break-all",
                                            maxWidth: "100%",
                                            overflowWrap: "break-word",
                                          }}
                                        >
                                          {highlightMatch(
                                            instance.url,
                                            searchTerm
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Box
                                          sx={{
                                            backgroundColor: "#e1e1e1",
                                            borderRadius: 1,
                                            px: 2,
                                            py: 1,
                                            display: "inline-block",
                                          }}
                                        >
                                          {highlightMatch(
                                            instance.evidence,
                                            searchTerm
                                          )}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={3} align="center">
                                      No instances available
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box> */}

                        {/* //Instances */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            mt: 3,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "20px",
                              fontWeight: "700",
                              color: "#333", // Darker for better contrast
                              mb: 1,
                            }}
                          >
                            URL
                          </Typography>

                          <Paper
                            elevation={1}
                            sx={{
                              p: 1.5,
                              backgroundColor: "#F0F4FF",
                              borderRadius: "10px",
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                              fontSize: "14px",
                              color: "#1976d2",
                              wordBreak: "break-word",
                            }}
                          >
                            {vuln.origin + vuln.path}
                          </Paper>
                        </Box>

                        {/* Solution */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            mt: 3,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "20px",
                              fontWeight: "700",
                              color: "#666875",
                            }}
                          >
                            Recommended Solutions
                          </Typography>
                          <Box
                            component="ul"
                            p={1}
                            pl={2}
                            sx={{
                              backgroundColor: "#FEF2F2",
                              borderRadius: "8px",
                              listStyleType: "disc",
                              listStylePosition: "outside", // Changed from 'inside'
                              borderLeft: "8px solid #FF0000",
                              lineHeight: 1.7,
                              paddingLeft: "24px", // Better spacing for outside bullets
                              // marginLeft: "16px", // Ensure bullet stays to the left
                            }}
                          >
                            {formatSolutionList(
                              vuln.remediation_background
                            ).map((item, index) => (
                              <li key={index} style={{ fontSize: "15px" }}>
                                {highlightMatch(item, searchTerm)}
                              </li>
                            ))}
                          </Box>
                        </Box>

                        {/* Reference */}
                        {/* <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            mt: 3,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "20px",
                              fontWeight: "700",
                              color: "#333", // Darker for better contrast
                              mb: 1,
                            }}
                          >
                            References
                          </Typography>

                          <Paper
                            elevation={1}
                            sx={{
                              p: 1.5,
                              backgroundColor: "#F0F4FF",
                              borderRadius: "10px",
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                              fontSize: "14px",
                              color: "#1976d2",
                              wordBreak: "break-word",
                            }}
                          >
                            {formatReferences(vuln.issue_background).map(
                              (ref, idx) =>
                                ref === "No reference provided" || !ref ? (
                                  <span
                                    key={idx}
                                    style={{
                                      color: "#1976d2",
                                      textDecoration: "none",
                                    }}
                                  >
                                    No reference provided
                                  </span>
                                ) : (
                                  <a
                                    key={idx}
                                    href={ref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#1976d2",
                                      textDecoration: "underline",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {ref}
                                  </a>
                                )
                            )}
                          </Paper>
                        </Box> */}
                      </Box>
                    ))
                ) : (
                  <Typography
                    sx={{
                      fontSize: "18px",
                      color: "#666875",
                      mt: 4,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    No vulnerabilities found for the selected severity.
                  </Typography>
                )
              ) : (
                <Typography sx={{ fontSize: "18px", color: "#666875", mt: 4 }}>
                  No data found or invalid format.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BurpSuiteViewDashboard;
